import React, { useState, useEffect } from 'react';
import {
    Plus, Calendar, Loader2, MessageCircle, X, Save,
    XCircle, Search, FileSpreadsheet, Edit3,
    Hash, Tag, FileText, CheckCircle, AlertCircle
} from 'lucide-react';
import { apiFetch } from '../../api';
import { ensureSocketConnection } from '../../websocket/socketUtils';
import { useWebSocket } from '../../websocket/WebSocketContext';

// ─── Socket helper (same pattern as whatsappConfigService.js) ─────────────────
const parseSocket = (msg) => {
    const payload = JSON.parse(msg.body);
    return typeof payload === 'string' ? JSON.parse(payload) : payload;
};

// ─── Endpoints (mirroring the service file style) ─────────────────────────────
const TEMPLATE_ENDPOINTS = {
    getAll: {
        rest: '/getWhatsAppTemplate',
        socketApp: '/app/getWhatsAppTemplate',
        socketTopic: (loginId) => `/topic/getWhatsAppTemplate/${loginId}`,
        method: 'GET',
    },
    save: {
        rest: '/saveWhatsAppTemplate',
        socketApp: '/app/saveWhatsAppTemplate',
        socketTopic: (loginId) => `/topic/saveWhatsAppTemplate/${loginId}`,
        method: 'POST',
    },
    update: {
        rest: '/updateWhatsAppTemplate',
        socketApp: '/app/updateWhatsAppTemplate',
        socketTopic: (loginId) => `/topic/updateWhatsAppTemplate/${loginId}`,
        method: 'PUT',
    },
};

// ─── Service functions ────────────────────────────────────────────────────────

const fetchAllTemplates = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(TEMPLATE_ENDPOINTS.getAll.socketTopic(loginId), (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data.data || []);
                else reject(new Error(data.message || 'Failed to fetch templates'));
            });
            client.publish({ destination: TEMPLATE_ENDPOINTS.getAll.socketApp, body: JSON.stringify({}) });
        });
    }
    const res = await apiFetch(TEMPLATE_ENDPOINTS.getAll.rest, { method: 'GET' });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data.data || [];
    throw new Error(data.message || 'Failed to fetch templates');
};

const saveTemplate = async ({ deliveryMode, client, loginId, body }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(TEMPLATE_ENDPOINTS.save.socketTopic(loginId), (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.message || 'Failed to save template'));
            });
            client.publish({ destination: TEMPLATE_ENDPOINTS.save.socketApp, body: JSON.stringify(body) });
        });
    }
    const res = await apiFetch(TEMPLATE_ENDPOINTS.save.rest, {
        method: TEMPLATE_ENDPOINTS.save.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.message || 'Failed to save template');
};

const updateTemplate = async ({ deliveryMode, client, loginId, body }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(TEMPLATE_ENDPOINTS.update.socketTopic(loginId), (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.message || 'Failed to update template'));
            });
            client.publish({ destination: TEMPLATE_ENDPOINTS.update.socketApp, body: JSON.stringify(body) });
        });
    }
    const res = await apiFetch(TEMPLATE_ENDPOINTS.update.rest, {
        method: TEMPLATE_ENDPOINTS.update.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.message || 'Failed to update template');
};

// ─── Alert Banner ─────────────────────────────────────────────────────────────
function AlertBanner({ success, error, onClear }) {
    if (!success && !error) return null;
    return (
        <div className={`flex items-center space-x-3 p-4 rounded-xl border mb-6 ${success
                ? 'bg-green-500/10 border-green-500/30 text-green-200'
                : 'bg-red-500/10 border-red-500/30 text-red-200'
            }`}>
            {success
                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm font-medium flex-1">{success || error}</span>
            <button onClick={onClear} className="text-current opacity-60 hover:opacity-100 transition-opacity">
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}

// ─── Template Card ────────────────────────────────────────────────────────────
function TemplateCard({ data, onEdit }) {
    return (
        <div className="group relative bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/30 rounded-2xl p-5 transition-all duration-300 backdrop-blur-sm flex flex-col justify-between h-full">

            <div className="flex justify-between items-start mb-4">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-300 border border-white/10 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {data.creation_time_str || 'Unknown Date'}
                </span>
                <button
                    onClick={() => onEdit(data)}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-400 hover:bg-white/5 transition-all"
                    title="Edit template"
                >
                    <Edit3 className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                    <Hash className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-xs text-slate-500 mb-0.5">Campaign ID</p>
                        <p className="text-sm text-slate-200 font-mono break-all">{data.campaignId}</p>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <Tag className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-xs text-slate-500 mb-0.5">Template ID</p>
                        <p className="text-sm text-slate-200 font-mono break-all">{data.templateId}</p>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-xs text-slate-500 mb-0.5">Message</p>
                        <p className="text-sm text-slate-200 leading-relaxed font-mono bg-black/20 p-2.5 rounded-lg border border-white/5 break-words">
                            {data.message}
                        </p>
                    </div>
                </div>
            </div>

            <div className="pt-3 border-t border-white/5">
                <span className="font-mono text-xs text-slate-600 truncate block" title={data._id}>
                    ID: {data._id}
                </span>
            </div>
        </div>
    );
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────
function TemplateModal({ isOpen, onClose, onSuccess, editData, deliveryMode, client, loginId }) {
    const isEditing = !!editData;
    const emptyForm = { campaignId: '', templateId: '', message: '' };
    const [form, setForm] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setForm(isEditing
                ? { campaignId: editData.campaignId || '', templateId: editData.templateId || '', message: editData.message || '' }
                : emptyForm
            );
            setError('');
        }
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate
        for (const [key, val] of Object.entries(form)) {
            if (!val.trim()) {
                setError(`${key.replace(/([A-Z])/g, ' $1')} cannot be empty.`);
                return;
            }
        }

        setIsSubmitting(true);
        setError('');

        try {
            const ctx = { deliveryMode, client, loginId };

            if (isEditing) {
                await updateTemplate({ ...ctx, body: { id: editData._id, ...form } });
            } else {
                await saveTemplate({ ...ctx, body: { ...form } });
            }

            onSuccess(isEditing ? 'Template updated successfully!' : 'Template saved successfully!');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fields = [
        { key: 'campaignId', label: 'Campaign ID', icon: Hash, placeholder: 'Enter campaign ID...', color: 'text-emerald-400' },
        { key: 'templateId', label: 'Template ID', icon: Tag, placeholder: 'Enter template ID...', color: 'text-teal-400' },
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-white/10 max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">

                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 opacity-50 pointer-events-none" />

                {/* Header */}
                <div className="relative p-6 pb-4 shrink-0">
                    <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                            <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">
                                {isEditing ? 'Edit Template' : 'Add WhatsApp Template'}
                            </h3>
                            <p className="text-white/60 text-sm">
                                {isEditing ? 'Update the template details below.' : 'Fill in the details for your new template.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="relative p-6 pt-2 overflow-y-auto flex-1">
                    <form id="wa-template-form" onSubmit={handleSubmit} className="space-y-4">
                        {fields.map(({ key, label, icon: Icon, placeholder, color }) => (
                            <div key={key}>
                                <label className="block text-sm font-medium text-white/80 mb-1.5 flex items-center gap-1.5">
                                    <Icon className={`w-4 h-4 ${color}`} /> {label} *
                                </label>
                                <input
                                    type="text"
                                    value={form[key]}
                                    onChange={e => handleChange(key, e.target.value)}
                                    placeholder={placeholder}
                                    disabled={isSubmitting}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all text-sm"
                                />
                            </div>
                        ))}

                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-1.5 flex items-center gap-1.5">
                                <FileText className="w-4 h-4 text-cyan-400" /> Message *
                            </label>
                            <textarea
                                value={form.message}
                                onChange={e => handleChange('message', e.target.value)}
                                placeholder="Enter your WhatsApp message template..."
                                rows={4}
                                disabled={isSubmitting}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all resize-none text-sm font-mono"
                            />
                            <p className="text-xs text-slate-500 mt-1">{form.message.length} characters</p>
                        </div>

                        {error && (
                            <div className="flex items-start space-x-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-red-200">{error}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <div className="relative p-6 pt-0 shrink-0">
                    <div className="flex space-x-3 pt-4 border-t border-white/5">
                        <button type="button" onClick={onClose} disabled={isSubmitting}
                            className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-medium transition-all border border-white/10 disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" form="wa-template-form" disabled={isSubmitting}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium shadow-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSubmitting
                                ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Saving...</span></>
                                : <><Save className="w-5 h-5" /><span>{isEditing ? 'Update Template' : 'Save Template'}</span></>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WhatsappTemplateManager({ loginId, deliveryMode }) {
    const { client } = useWebSocket();

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const data = await fetchAllTemplates({ deliveryMode, client, loginId });
            setTemplates(data);
        } catch (err) {
            console.error('Error fetching WA templates:', err);
            setError(err.message || 'Network error. Could not load templates.');
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTemplates(); }, [deliveryMode]);

    // ── Search ────────────────────────────────────────────────────────────────
    const filteredTemplates = templates.filter(t => {
        const q = searchQuery.toLowerCase();
        return (
            (t.campaignId || '').toLowerCase().includes(q) ||
            (t.templateId || '').toLowerCase().includes(q) ||
            (t.message || '').toLowerCase().includes(q)
        );
    });

    // ── Export CSV ────────────────────────────────────────────────────────────
    const handleExport = () => {
        if (!filteredTemplates.length) return;
        const headers = ['ID', 'Campaign ID', 'Template ID', 'Message', 'Date'];
        const rows = filteredTemplates.map(t => [
            t._id, t.campaignId, t.templateId,
            `"${(t.message || '').replace(/"/g, '""')}"`,
            t.creation_time_str || ''
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `whatsapp_templates_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── Modal handlers ────────────────────────────────────────────────────────
    const openAdd = () => { setEditData(null); setIsModalOpen(true); };
    const openEdit = (t) => { setEditData(t); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditData(null); };

    const handleModalSuccess = (msg) => {
        closeModal();
        setSuccess(msg);
        fetchTemplates();
    };

    const clearMessages = () => { setSuccess(''); setError(''); };

    useEffect(() => {
        if (!success && !error) return;
        const t = setTimeout(clearMessages, 4000);
        return () => clearTimeout(t);
    }, [success, error]);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 p-6 md:p-8 font-sans">

            {/* Header */}
            <div className="max-w-6xl mx-auto mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <MessageCircle className="w-8 h-8 text-emerald-500" />
                        WhatsApp Templates
                    </h1>
                    <p className="text-slate-400">Manage your WhatsApp message templates</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={handleExport} disabled={filteredTemplates.length === 0}
                        className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded-xl font-medium border border-white/10 flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Export to CSV">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                        <span className="hidden sm:inline">Export</span>
                    </button>
                    <button onClick={openAdd}
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-emerald-500/20 flex items-center gap-2 transition-all">
                        <Plus className="w-5 h-5" />
                        <span className="hidden sm:inline">Add Template</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                </div>
            </div>

            {/* Alerts */}
            <div className="max-w-6xl mx-auto">
                <AlertBanner success={success} error={error} onClear={clearMessages} />
            </div>

            {/* Search & Stats */}
            <div className="max-w-6xl mx-auto mb-8 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-4 justify-between items-center backdrop-blur-sm">
                <div className="relative w-full sm:max-w-md group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-400 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search by campaign ID, template ID, or message..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                    />
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-white/5">
                        <span>Total:</span>
                        <span className="text-white font-mono font-bold">{filteredTemplates.length}</span>
                    </div>
                    <button onClick={fetchTemplates} disabled={loading}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-white/10 rounded-lg text-sm text-slate-300 transition-all disabled:opacity-50">
                        <Loader2 className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                    </div>
                ) : filteredTemplates.length === 0 ? (
                    <div className="text-center py-20 bg-slate-900/30 rounded-2xl border border-white/5 border-dashed">
                        {searchQuery ? (
                            <>
                                <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-slate-300">No matching templates</h3>
                                <p className="text-slate-500 mt-2">No templates found matching "{searchQuery}"</p>
                            </>
                        ) : (
                            <>
                                <MessageCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-slate-300">No templates found</h3>
                                <p className="text-slate-500 mt-2 mb-6">Get started by adding your first WhatsApp template.</p>
                                <button onClick={openAdd}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium transition-all">
                                    <Plus className="w-4 h-4" /> Add First Template
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTemplates.map(t => (
                            <TemplateCard key={t._id} data={t} onEdit={openEdit} />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal — passes deliveryMode, client, loginId down */}
            <TemplateModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSuccess={handleModalSuccess}
                editData={editData}
                deliveryMode={deliveryMode}
                client={client}
                loginId={loginId}
            />
        </div>
    );
}